import { useMemo } from 'react';

import { useDataset, useExamples } from '@/hooks/useSwr';

import { useGraphAssistantsDebugSchema } from '../api/useGraphSwr';
import { useAssistantStore } from '../store/assistants/assistantsStore';

export const useDatasetIsCompatibleWithSchema = ({
  datasetId,
  splits,
}: {
  datasetId?: string;
  splits?: string[];
}) => {
  const { data: dataset } = useDataset(datasetId);
  const { inputs_schema_definition: datasetInputSchema } = dataset ?? {};
  const { data: examples } = useExamples(
    datasetId
      ? {
          dataset: datasetId,
          splits: splits?.length ? splits : undefined,
        }
      : null,
    undefined,
    { keepPreviousData: false }
  );

  const activeAssistantId = useAssistantStore(
    (state) => state.activeAssistantId
  );
  const { data: schemas } = useGraphAssistantsDebugSchema(
    activeAssistantId ? [activeAssistantId] : null
  );

  const schema = schemas?.[0];
  const { inputRoot } = schema ?? {};

  const { isCompatible, missingFields } = useMemo(() => {
    const requiredFields = inputRoot?.required ?? [];

    if (
      datasetInputSchema &&
      'properties' in datasetInputSchema &&
      typeof datasetInputSchema.properties === 'object' &&
      datasetInputSchema.properties !== null
    ) {
      const properties = datasetInputSchema.properties;
      const propertiesKeys = new Set(Object.keys(properties));
      return {
        isCompatible: requiredFields.every((field) =>
          propertiesKeys.has(field)
        ),
        missingFields: requiredFields.filter(
          (field) => !propertiesKeys.has(field)
        ),
      };
    }

    const firstExample = examples?.[0];
    const exampleInputKeys = new Set(Object.keys(firstExample?.inputs ?? {}));
    return {
      isCompatible: requiredFields.every((field) =>
        exampleInputKeys.has(field)
      ),
      missingFields: requiredFields.filter(
        (field) => !exampleInputKeys.has(field)
      ),
    };
  }, [examples, inputRoot, datasetInputSchema]);

  return { isCompatible, missingFields };
};
